'use client';

import { useState } from 'react';
import { PageHeader } from '../../components/shared/PageHeader';
import { DataTable } from '../../components/shared/DataTable';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { CurrencyDisplay } from '../../components/shared/CurrencyDisplay';
import type { Status } from '../../types';
import {
  ShoppingCart,
  Package,
  TrendingUp,
  TrendingDown,
  Users,
  ArrowRight,
  ClipboardList,
  AlertTriangle,
} from 'lucide-react';

// 대시보드 데이터 타입
interface RecentOrder {
  id: string;
  orderNo: string;
  customer: string;
  amount: number;
  status: Status;
  date: string;
}

interface LowStockItem {
  id: string;
  itemCode: string;
  itemName: string;
  currentStock: number;
  minStock: number;
  warehouse: string;
}

// 목업 데이터 - 농산물 박스 제조업
const recentOrders: RecentOrder[] = [
  { id: '1', orderNo: 'SO-2025-001', customer: '서귀포감귤농협', amount: 8800000, status: 'completed', date: '2025-01-28' },
  { id: '2', orderNo: 'SO-2025-002', customer: '논산딸기영농조합', amount: 5500000, status: 'pending', date: '2025-01-28' },
  { id: '3', orderNo: 'SO-2025-003', customer: '충주사과영농조합', amount: 4200000, status: 'approved', date: '2025-01-27' },
  { id: '4', orderNo: 'SO-2025-004', customer: '나주배작목반', amount: 6800000, status: 'draft', date: '2025-01-27' },
  { id: '5', orderNo: 'SO-2025-005', customer: '가락시장농산물유통', amount: 3500000, status: 'completed', date: '2025-01-26' },
];

const lowStockItems: LowStockItem[] = [
  { id: '1', itemCode: 'CB-E120', itemName: '골판지 원단 E골 120g', currentStock: 50, minStock: 200, warehouse: '원자재 창고' },
  { id: '2', itemCode: 'LN-K180', itemName: '크라프트 라이너 180g', currentStock: 80, minStock: 300, warehouse: '원자재 창고' },
  { id: '3', itemCode: 'BOX-S03', itemName: '딸기박스 3kg', currentStock: 120, minStock: 500, warehouse: '완제품 창고' },
  { id: '4', itemCode: 'SUP-INK-R', itemName: '인쇄잉크 RED', currentStock: 5, minStock: 20, warehouse: '부자재 창고' },
];

type TabType = 'orders' | 'stock';

export function Dashboard() {
  const [activeTab, setActiveTab] = useState<TabType>('orders');

  const orderColumns = [
    {
      key: 'customer',
      label: '고객',
      minWidth: '120px',
      render: (value: unknown) => (
        <span className="truncate block max-w-[150px]" title={value as string}>{value as string}</span>
      ),
    },
    {
      key: 'amount',
      label: '금액',
      minWidth: '100px',
      align: 'right' as const,
      render: (value: unknown) => <CurrencyDisplay amount={value as number} />,
    },
    {
      key: 'status',
      label: '상태',
      minWidth: '70px',
      render: (value: unknown) => <StatusBadge status={value as Status} />,
    },
    {
      key: 'date',
      label: '날짜',
      minWidth: '90px',
      render: (value: unknown) => (
        <span className="whitespace-nowrap">{value as string}</span>
      ),
    },
  ];

  const stockColumns = [
    {
      key: 'itemName',
      label: '품목명',
      minWidth: '150px',
    },
    {
      key: 'currentStock',
      label: '현재',
      align: 'right' as const,
      minWidth: '60px',
      render: (value: unknown, row: LowStockItem) => (
        <span className={row.currentStock < row.minStock ? 'text-red-500 font-medium' : ''}>
          {value as number}
        </span>
      ),
    },
    {
      key: 'minStock',
      label: '최소',
      align: 'right' as const,
      minWidth: '60px',
    },
    {
      key: 'warehouse',
      label: '창고',
      minWidth: '100px',
    },
  ];

  return (
    <div>
      <PageHeader
        title="대시보드"
        description="Box ERP 시스템 현황을 한눈에 확인하세요."
      />

      {/* 요약 카드 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm text-muted-foreground">오늘 박스 출하</p>
              <p className="text-xl font-bold mt-1">₩28,800,000</p>
              <div className="flex items-center gap-1 mt-1.5 text-xs text-green-500">
                <TrendingUp className="w-3 h-3" />
                <span>+15.2%</span>
                <span className="text-muted-foreground">전일 대비</span>
              </div>
            </div>
            <div className="w-9 h-9 flex-shrink-0 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm text-muted-foreground">생산 주문</p>
              <p className="text-xl font-bold mt-1">18건</p>
              <p className="text-xs text-muted-foreground mt-1">제작 대기 6건</p>
              <div className="flex items-center gap-1 mt-1 text-xs text-green-500">
                <TrendingUp className="w-3 h-3" />
                <span>+12.5%</span>
                <span className="text-muted-foreground">전일 대비</span>
              </div>
            </div>
            <div className="w-9 h-9 flex-shrink-0 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <ShoppingCart className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </div>

        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm text-muted-foreground">원자재 부족</p>
              <p className="text-xl font-bold mt-1">4건</p>
              <p className="text-xs text-muted-foreground mt-1">긴급 발주 필요</p>
              <div className="flex items-center gap-1 mt-1 text-xs text-red-500">
                <TrendingDown className="w-3 h-3" />
                <span>-20%</span>
                <span className="text-muted-foreground">전주 대비</span>
              </div>
            </div>
            <div className="w-9 h-9 flex-shrink-0 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <Package className="w-4 h-4 text-red-600 dark:text-red-400" />
            </div>
          </div>
        </div>

        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm text-muted-foreground">거래 농협/조합</p>
              <p className="text-xl font-bold mt-1">42개</p>
              <p className="text-xs text-muted-foreground mt-1">이번 달 출하</p>
              <div className="flex items-center gap-1 mt-1 text-xs text-green-500">
                <TrendingUp className="w-3 h-3" />
                <span>+8.5%</span>
                <span className="text-muted-foreground">전월 대비</span>
              </div>
            </div>
            <div className="w-9 h-9 flex-shrink-0 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <Users className="w-4 h-4 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>
      </div>

      {/* 탭 형태 테이블 섹션 */}
      <div className="bg-card border border-border rounded-lg mb-4 sm:mb-6">
        {/* 탭 헤더 - 모바일 반응형 */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-border">
          <div className="flex overflow-x-auto scrollbar-none">
            <button
              onClick={() => setActiveTab('orders')}
              className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium border-b-2 transition-all whitespace-nowrap ${
                activeTab === 'orders'
                  ? 'border-primary text-primary bg-primary/5'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-accent'
              }`}
            >
              <ClipboardList className="w-4 h-4" />
              <span>최근 주문</span>
              <span className={`px-1.5 py-0.5 text-xs rounded ${
                activeTab === 'orders'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              }`}>5</span>
            </button>
            <button
              onClick={() => setActiveTab('stock')}
              className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium border-b-2 transition-all whitespace-nowrap ${
                activeTab === 'stock'
                  ? 'border-primary text-primary bg-primary/5'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-accent'
              }`}
            >
              <AlertTriangle className="w-4 h-4" />
              <span>재고 부족</span>
              <span className={`px-1.5 py-0.5 text-xs rounded ${
                activeTab === 'stock'
                  ? 'bg-red-500 text-white'
                  : 'bg-muted text-muted-foreground'
              }`}>4</span>
            </button>
          </div>
          <button className="hidden sm:flex text-sm text-primary hover:underline items-center gap-1 pr-4">
            전체 보기
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* 탭 컨텐츠 */}
        <div className="p-2 sm:p-4">
          {activeTab === 'orders' ? (
            <DataTable
              data={recentOrders}
              columns={orderColumns}
              pageSize={5}
              showPagination={false}
              minTableWidth="420px"
            />
          ) : (
            <DataTable
              data={lowStockItems}
              columns={stockColumns}
              pageSize={5}
              showPagination={false}
              minTableWidth="400px"
            />
          )}
        </div>
      </div>

      {/* 빠른 액션 */}
      <div className="bg-card border border-border rounded-lg p-3 sm:p-4">
        <h2 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base">빠른 작업</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
          <QuickActionButton
            icon={<ShoppingCart className="w-4 h-4 sm:w-5 sm:h-5" />}
            label="박스 주문 등록"
          />
          <QuickActionButton
            icon={<Package className="w-4 h-4 sm:w-5 sm:h-5" />}
            label="원자재 재고"
          />
          <QuickActionButton
            icon={<TrendingUp className="w-4 h-4 sm:w-5 sm:h-5" />}
            label="생산 현황"
          />
          <QuickActionButton
            icon={<Users className="w-4 h-4 sm:w-5 sm:h-5" />}
            label="농협/조합 관리"
          />
        </div>
      </div>
    </div>
  );
}

function QuickActionButton({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg border border-border hover:bg-accent transition-colors">
      <div className="p-1.5 sm:p-2 bg-primary/10 rounded-lg text-primary shrink-0">{icon}</div>
      <span className="text-xs sm:text-sm font-medium text-left truncate">{label}</span>
    </button>
  );
}
